import React from 'react';
import { Calendar } from 'lucide-react';
import {
    PlatformConnectionGate,
    MetaTargetingPanel,
    GoogleTargetingPanel,
    TikTokTargetingPanel,
    LinkedInTargetingPanel
} from '../targeting';

// Default targeting structure
export const getDefaultTargeting = () => ({
    // Audience strategy: 'advantage_plus' (Meta AI), 'custom', 'lookalike'
    // Default to advantage_plus for Meta's AI-optimized targeting
    audienceStrategy: 'advantage_plus',
    demographics: {
        ageMin: 18,
        ageMax: 65,
        genders: ['all']
    },
    locations: {
        included: [],
        excluded: []
    },
    detailedTargeting: {
        meta: { interests: [], behaviors: [] },
        google: { audienceSegments: { affinity: [], inMarket: [] }, keywords: [] },
        tiktok: { interestCategories: [], behaviors: [] },
        linkedin: { company: {}, job: {}, skills: [] }
    },
    audiences: {
        included: [],
        excluded: [],
        lookalikes: []
    },
    estimate: null
});

/**
 * AdSetForm - Reusable form component for AdSet node editing
 * Used in both AdSetModal and PropertiesPanel
 */
const AdSetForm = ({
    formData,
    onChange,
    errors = {},
    platform = 'meta',
    platformConnection = null,
    onConnectPlatform,
    campaignType = null,
    brandContext = null
}) => {
    const handleChange = (field, value) => {
        onChange(field, value);
    };

    const handleTargetingChange = (newTargeting) => {
        onChange('targeting', newTargeting);
    };

    const handleScheduleChange = (field, value) => {
        onChange('schedule', { ...formData.schedule, [field]: value });
    };

    // Platform-specific panel component
    const renderTargetingPanel = () => {
        const isConnected = platformConnection?.isConnected || false;
        const accountId = platformConnection?.accountId || null;
        const accountName = platformConnection?.accountName || null;

        // Common props for all panels
        const panelProps = {
            brandId: brandContext?.id,
            userId: brandContext?.userId,
            brand: brandContext, // Full brand object for AI suggestions
            targeting: formData.targeting || getDefaultTargeting(),
            onChange: handleTargetingChange,
            isConnected,
            accountId,
            funnelStage: formData.funnelStage || 'TOFU'
        };

        return (
            <PlatformConnectionGate
                platform={platform}
                isConnected={isConnected}
                accountName={accountName}
                onConnect={onConnectPlatform}
            >
                {platform === 'meta' && <MetaTargetingPanel {...panelProps} />}
                {platform === 'google' && <GoogleTargetingPanel {...panelProps} campaignType={campaignType} />}
                {platform === 'tiktok' && <TikTokTargetingPanel {...panelProps} />}
                {platform === 'linkedin' && <LinkedInTargetingPanel {...panelProps} />}
            </PlatformConnectionGate>
        );
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Ad Set Name */}
            <div className="input-group">
                <label className="input-label">Ad Set Name *</label>
                <input
                    type="text"
                    className="input-field"
                    placeholder="e.g., 25-35 Males - Fitness Enthusiasts"
                    value={formData.name || ''}
                    onChange={(e) => handleChange('name', e.target.value)}
                    style={errors.name ? { borderColor: 'var(--color-error)' } : {}}
                />
                {errors.name && (
                    <span style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-xs)' }}>
                        {errors.name}
                    </span>
                )}
            </div>

            {/* Platform-Specific Targeting Panel */}
            <div className="input-group">
                <label className="input-label" style={{ marginBottom: 'var(--space-3)' }}>
                    Audience Targeting
                </label>
                {errors.locations && (
                    <div style={{
                        color: 'var(--color-error)',
                        fontSize: 'var(--font-size-xs)',
                        marginBottom: 'var(--space-2)'
                    }}>
                        {errors.locations}
                    </div>
                )}
                {renderTargetingPanel()}
            </div>

            {/* Schedule */}
            <div className="input-group">
                <label className="input-label">
                    <Calendar size={14} style={{ marginRight: 'var(--space-1)' }} />
                    Schedule
                </label>
                <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
                    <div style={{ flex: 1 }}>
                        <label style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            marginBottom: 'var(--space-1)',
                            display: 'block'
                        }}>
                            Start Date
                        </label>
                        <input
                            type="date"
                            className="input-field"
                            value={formData.schedule?.start || ''}
                            onChange={(e) => handleScheduleChange('start', e.target.value)}
                        />
                    </div>
                    <div style={{ flex: 1 }}>
                        <label style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            marginBottom: 'var(--space-1)',
                            display: 'block'
                        }}>
                            End Date
                        </label>
                        <input
                            type="date"
                            className="input-field"
                            value={formData.schedule?.end || ''}
                            onChange={(e) => handleScheduleChange('end', e.target.value)}
                        />
                    </div>
                </div>
            </div>

            {/* Optimization */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
                <div className="input-group">
                    <label className="input-label">Bid Strategy</label>
                    <select
                        className="select-field"
                        value={formData.bidStrategy || 'lowest_cost'}
                        onChange={(e) => handleChange('bidStrategy', e.target.value)}
                    >
                        <option value="lowest_cost">Lowest Cost</option>
                        <option value="cost_cap">Cost Cap</option>
                        <option value="bid_cap">Bid Cap</option>
                        <option value="target_cost">Target Cost</option>
                    </select>
                </div>
                <div className="input-group">
                    <label className="input-label">Optimization Goal</label>
                    <select
                        className="select-field"
                        value={formData.optimizationGoal || 'conversions'}
                        onChange={(e) => handleChange('optimizationGoal', e.target.value)}
                    >
                        <option value="conversions">Conversions</option>
                        <option value="link_clicks">Link Clicks</option>
                        <option value="impressions">Impressions</option>
                        <option value="landing_page_views">Landing Page Views</option>
                        <option value="reach">Reach</option>
                    </select>
                </div>
            </div>
        </div>
    );
};

export default AdSetForm;
